export const CANONICAL_RELATIONSHIP_REVIEW_DECISIONS = Object.freeze([
  "pending",
  "approved",
  "rejected",
  "changes_requested",
] as const);

export type CanonicalRelationshipReviewDecision =
  typeof CANONICAL_RELATIONSHIP_REVIEW_DECISIONS[number];

export type CanonicalRelationshipDecision =
  Exclude<CanonicalRelationshipReviewDecision, "pending">;

export type CanonicalRelationshipReviewerRole =
  | "super_admin"
  | "admin"
  | "ops_manager"
  | "owner"
  | "custom";

export type CanonicalRelationshipReviewQueueItem = Readonly<{
  reviewId: string;
  organizationId: string;
  parentContractorId: string;
  childContractorId: string;
  reviewStatus: string;
  reviewDecision: CanonicalRelationshipReviewDecision;
  latestDecisionId: string | null;
  decidedAt: string | null;
  requiredCount: number;
  activeDocumentCount: number;
  prerequisiteBlocker: string | null;
}>;

export type CanonicalRelationshipReviewRequirement = Readonly<{
  id: string;
  documentType: string;
  label: string;
  required: boolean;
  sortOrder: number;
}>;

export type CanonicalRelationshipReviewDocument = Readonly<{
  id: string;
  documentType: string;
  label: string;
  fileName: string;
  mimeType: string;
  byteSize: number;
  status: string;
  createdAt: string;
}>;

export type CanonicalRelationshipReviewDocumentDetail = Readonly<{
  reviewId: string;
  relationshipRequirementId: string;
  documentId: string;
  fileName: string;
  mimeType: string;
  byteSize: number;
  submittedAt: string;
  issueDate: string | null;
  expiresAt: string | null;
  note: string | null;
  documentStatus: string;
  currentDecision: CanonicalRelationshipReviewDecision;
  latestDecisionId: string | null;
  canPreview: boolean;
  blocker: string | null;
}>;

export type CanonicalRelationshipReviewDocumentHistoryItem = Readonly<{
  decisionId: string;
  reviewId: string;
  relationshipRequirementId: string;
  documentId: string;
  decision: CanonicalRelationshipDecision;
  reasonCode: string | null;
  note: string | null;
  reviewerRole: CanonicalRelationshipReviewerRole;
  reviewerScopeId: string | null;
  previousDecisionId: string | null;
  decidedAt: string;
}>;

export type CanonicalRelationshipReviewDocumentHistory = Readonly<{
  reviewId: string;
  relationshipRequirementId: string;
  documentId: string;
  decisions: readonly CanonicalRelationshipReviewDocumentHistoryItem[];
}>;

export type CanonicalRelationshipReviewDocumentDecisionResult = Readonly<{
  decisionId: string;
  reviewId: string;
  relationshipRequirementId: string;
  documentId: string;
  decision: CanonicalRelationshipDecision;
  reasonCode: string | null;
  note: string | null;
  reviewerUserId: string;
  reviewerScopeId: string | null;
  reviewerRole: CanonicalRelationshipReviewerRole;
  decidedAt: string;
  previousDecisionId: string | null;
  requestId: string | null;
}>;

export type CanonicalRelationshipReviewDocumentPreview = Readonly<{
  signedUrl: string;
  expiresAt: string;
  fileName: string;
  mimeType: string;
  byteSize: number;
}>;

export type CanonicalRelationshipReviewDetail =
  CanonicalRelationshipReviewQueueItem & Readonly<{
    requirements: readonly CanonicalRelationshipReviewRequirement[];
    documents: readonly CanonicalRelationshipReviewDocument[];
  }>;

export type CanonicalRelationshipReviewHistoryItem = Readonly<{
  decisionId: string;
  reviewId: string;
  decision: Exclude<CanonicalRelationshipReviewDecision, "pending">;
  reasonCode: string | null;
  note: string | null;
  reviewerRole: CanonicalRelationshipReviewerRole;
  reviewerScopeId: string | null;
  previousDecisionId: string | null;
  decidedAt: string;
}>;

export type CanonicalRelationshipReviewHistory = Readonly<{
  reviewId: string;
  decisions: readonly CanonicalRelationshipReviewHistoryItem[];
}>;

export type CanonicalRelationshipReviewQueue = Readonly<{
  items: readonly CanonicalRelationshipReviewQueueItem[];
  nextCursor: null;
}>;

export type CanonicalRelationshipDecisionResult = Readonly<{
  decisionId: string;
  reviewId: string;
  decision: CanonicalRelationshipDecision;
  reasonCode: string | null;
  note: string | null;
  reviewerUserId: string;
  reviewerScopeId: string | null;
  reviewerRole: CanonicalRelationshipReviewerRole;
  decidedAt: string;
  previousDecisionId: string | null;
  requestId: string | null;
}>;

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SAFE_CODE = /^[a-z][a-z0-9_]{0,127}$/;
const SAFE_REASON = /^[a-z][a-z0-9_]{0,63}$/;
const SAFE_IDEMPOTENCY_KEY = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const UNSAFE_NOTE =
  /<[^>]+>|https?:\/\/|authorization\s*:|bearer\s+|service[_ -]?role|refresh[_ -]?token|api[_ -]?key/i;
const ROLES = new Set<CanonicalRelationshipReviewerRole>([
  "super_admin",
  "admin",
  "ops_manager",
  "owner",
  "custom",
]);
const DECISIONS = new Set(["approved", "rejected", "changes_requested"]);
const QUEUE_KEYS = [
  "reviewId", "organizationId", "parentContractorId", "childContractorId",
  "reviewStatus", "reviewDecision", "latestDecisionId", "decidedAt",
  "requiredCount", "activeDocumentCount", "prerequisiteBlocker",
] as const;

export class CanonicalRelationshipReviewContractError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = "CanonicalRelationshipReviewContractError";
  }
}

function invalid(code = "canonical_relationship_review_response_invalid"): never {
  throw new CanonicalRelationshipReviewContractError(code);
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) invalid();
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): void {
  const allowed = new Set(keys);
  if (
    Object.keys(value).some((key) => !allowed.has(key))
    || keys.some((key) => !(key in value))
  ) {
    invalid();
  }
}

function exactKeysAnyOf(
  value: Record<string, unknown>,
  keySets: readonly (readonly string[])[],
): void {
  if (keySets.some((keys) => {
    const allowed = new Set(keys);
    return !Object.keys(value).some((key) => !allowed.has(key))
      && !keys.some((key) => !(key in value));
  })) {
    return;
  }
  invalid();
}

function string(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) invalid();
  return value.trim();
}

function code(value: unknown): string {
  const result = string(value);
  if (!SAFE_CODE.test(result)) invalid();
  return result;
}

function nullableString(value: unknown): string | null {
  return value === null ? null : string(value);
}

function uuid(value: unknown): string {
  const result = string(value);
  if (!UUID.test(result)) invalid("canonical_relationship_review_uuid_invalid");
  return result;
}

function nullableUuid(value: unknown): string | null {
  return value === null ? null : uuid(value);
}

function firstDefined<T>(...values: readonly T[]): T | undefined {
  return values.find((value) => value !== undefined);
}

function count(value: unknown): number {
  if (
    typeof value !== "number"
    || !Number.isInteger(value)
    || value < 0
  ) {
    invalid();
  }
  return value;
}

function boolean(value: unknown): boolean {
  if (typeof value !== "boolean") invalid();
  return value;
}

export function assertCanonicalRelationshipReviewUuid(value: unknown): string {
  return uuid(value);
}

export function assertCanonicalRelationshipReviewLimit(value: unknown): number {
  if (
    typeof value !== "number"
    || !Number.isInteger(value)
    || value < 1
    || value > 100
  ) {
    invalid("canonical_relationship_review_limit_invalid");
  }
  return value;
}

export function validateCanonicalRelationshipDecisionInput(input: {
  decision: CanonicalRelationshipDecision;
  reasonCode?: string;
  note?: string;
  idempotencyKey: string;
}): Readonly<{
  decision: CanonicalRelationshipDecision;
  reasonCode?: string;
  note?: string;
  idempotencyKey: string;
}> {
  if (!DECISIONS.has(input.decision) || !SAFE_IDEMPOTENCY_KEY.test(input.idempotencyKey)) {
    invalid("canonical_relationship_decision_invalid");
  }
  const reasonCode = input.reasonCode?.trim() || undefined;
  if (
    (input.decision === "approved" && reasonCode !== undefined)
    || (input.decision !== "approved"
      && (!reasonCode || !SAFE_REASON.test(reasonCode)))
  ) invalid("canonical_relationship_reason_invalid");
  const note = input.note?.trim() || undefined;
  if (note && (note.length > 2000 || UNSAFE_NOTE.test(note))) {
    invalid("canonical_relationship_note_invalid");
  }
  if (reasonCode === "other" && !note) {
    invalid("canonical_relationship_reason_invalid");
  }
  return Object.freeze({
    decision: input.decision,
    ...(reasonCode ? { reasonCode } : {}),
    ...(note ? { note } : {}),
    idempotencyKey: input.idempotencyKey,
  });
}

export function parseCanonicalRelationshipReviewQueueItem(
  value: unknown,
): CanonicalRelationshipReviewQueueItem {
  const row = record(value);
  exactKeys(row, QUEUE_KEYS);
  if (
    !CANONICAL_RELATIONSHIP_REVIEW_DECISIONS.includes(
      row.reviewDecision as CanonicalRelationshipReviewDecision,
    )
  ) {
    invalid();
  }
  return Object.freeze({
    reviewId: uuid(row.reviewId),
    organizationId: uuid(row.organizationId),
    parentContractorId: uuid(row.parentContractorId),
    childContractorId: uuid(row.childContractorId),
    reviewStatus: code(row.reviewStatus),
    reviewDecision:
      row.reviewDecision as CanonicalRelationshipReviewDecision,
    latestDecisionId: nullableUuid(row.latestDecisionId),
    decidedAt: nullableString(row.decidedAt),
    requiredCount: count(row.requiredCount),
    activeDocumentCount: count(row.activeDocumentCount),
    prerequisiteBlocker: nullableString(row.prerequisiteBlocker),
  });
}

export function parseCanonicalRelationshipReviewQueue(
  value: unknown,
): CanonicalRelationshipReviewQueue {
  const response = record(value);
  exactKeys(response, ["items", "nextCursor"]);
  if (!Array.isArray(response.items) || response.nextCursor !== null) invalid();
  return Object.freeze({
    items: Object.freeze(
      response.items.map(parseCanonicalRelationshipReviewQueueItem),
    ),
    nextCursor: null,
  });
}

export function parseCanonicalRelationshipReviewDetail(
  value: unknown,
): CanonicalRelationshipReviewDetail {
  const response = record(value);
  exactKeys(response, [...QUEUE_KEYS, "requirements", "documents"]);
  const queue = parseCanonicalRelationshipReviewQueueItem(
    Object.fromEntries(QUEUE_KEYS.map((key) => [key, response[key]])),
  );
  if (!Array.isArray(response.requirements) || !Array.isArray(response.documents)) {
    invalid();
  }
  const requirements = response.requirements.map((value) => {
    const row = record(value);
    exactKeysAnyOf(row, [
      ["id", "doc_type", "label", "required", "sort_order"],
      ["id", "documentType", "label", "required", "sortOrder"],
    ]);
    if (typeof row.required !== "boolean") invalid();
    const documentType = firstDefined(row.documentType, row.doc_type);
    const sortOrder = firstDefined(row.sortOrder, row.sort_order);
    return Object.freeze({
      id: uuid(row.id),
      documentType: code(documentType),
      label: string(row.label),
      required: row.required,
      sortOrder: count(sortOrder),
    });
  });
  const documents = response.documents.map((value) => {
    const row = record(value);
    exactKeysAnyOf(row, [
      [
        "id", "doc_type", "label", "file_name", "mime_type", "byte_size",
        "status", "created_at",
      ],
      [
        "id", "documentType", "label", "fileName", "mimeType", "byteSize",
        "status", "createdAt",
      ],
    ]);
    const documentType = firstDefined(row.documentType, row.doc_type);
    const fileName = firstDefined(row.fileName, row.file_name);
    const mimeType = firstDefined(row.mimeType, row.mime_type);
    const byteSize = firstDefined(row.byteSize, row.byte_size);
    const createdAt = firstDefined(row.createdAt, row.created_at);
    return Object.freeze({
      id: uuid(row.id),
      documentType: code(documentType),
      label: string(row.label),
      fileName: string(fileName),
      mimeType: string(mimeType),
      byteSize: count(byteSize),
      status: code(row.status),
      createdAt: string(createdAt),
    });
  });
  return Object.freeze({
    ...queue,
    requirements: Object.freeze(requirements),
    documents: Object.freeze(documents),
  });
}

export function parseCanonicalRelationshipReviewHistory(
  value: unknown,
): CanonicalRelationshipReviewHistory {
  const response = record(value);
  exactKeys(response, ["reviewId", "decisions"]);
  if (!Array.isArray(response.decisions)) invalid();
  const reviewId = uuid(response.reviewId);
  const decisions = response.decisions.map((value) => {
    const row = record(value);
    exactKeys(row, [
      "id", "review_id", "decision", "reason_code", "note", "reviewer_role",
      "reviewer_scope_id", "previous_decision_id", "decided_at",
    ]);
    const decision = string(row.decision);
    const reviewerRole = string(row.reviewer_role);
    const reasonCode = nullableString(row.reason_code);
    if (
      !DECISIONS.has(decision)
      || !ROLES.has(reviewerRole)
      || (reasonCode !== null && !SAFE_CODE.test(reasonCode))
    ) {
      invalid();
    }
    return Object.freeze({
      decisionId: uuid(row.id),
      reviewId: uuid(row.review_id),
      decision:
        decision as CanonicalRelationshipReviewHistoryItem["decision"],
      reasonCode,
      note: nullableString(row.note),
      reviewerRole:
        reviewerRole as CanonicalRelationshipReviewHistoryItem["reviewerRole"],
      reviewerScopeId: nullableUuid(row.reviewer_scope_id),
      previousDecisionId: nullableUuid(row.previous_decision_id),
      decidedAt: string(row.decided_at),
    });
  });
  if (
    decisions.some((item) => item.reviewId !== reviewId)
    || decisions.some((item, index) =>
      index > 0 && decisions[index - 1].decidedAt > item.decidedAt
    )
  ) {
    invalid();
  }
  return Object.freeze({ reviewId, decisions: Object.freeze(decisions) });
}

export function parseCanonicalRelationshipReviewDocumentDetail(
  value: unknown,
): CanonicalRelationshipReviewDocumentDetail {
  const row = record(value);
  exactKeys(row, [
    "reviewId", "relationshipRequirementId", "documentId", "fileName",
    "mimeType", "byteSize", "submittedAt", "issueDate", "expiresAt", "note",
    "documentStatus", "currentDecision", "latestDecisionId", "canPreview",
    "blocker",
  ]);
  const currentDecision = string(row.currentDecision);
  if (
    !CANONICAL_RELATIONSHIP_REVIEW_DECISIONS.includes(
      currentDecision as CanonicalRelationshipReviewDecision,
    )
  ) {
    invalid();
  }
  return Object.freeze({
    reviewId: uuid(row.reviewId),
    relationshipRequirementId: uuid(row.relationshipRequirementId),
    documentId: uuid(row.documentId),
    fileName: string(row.fileName),
    mimeType: string(row.mimeType),
    byteSize: count(row.byteSize),
    submittedAt: string(row.submittedAt),
    issueDate: nullableString(row.issueDate),
    expiresAt: nullableString(row.expiresAt),
    note: nullableString(row.note),
    documentStatus: code(row.documentStatus),
    currentDecision:
      currentDecision as CanonicalRelationshipReviewDecision,
    latestDecisionId: nullableUuid(row.latestDecisionId),
    canPreview: boolean(row.canPreview),
    blocker: nullableString(row.blocker),
  });
}

export function parseCanonicalRelationshipReviewDocumentHistory(
  value: unknown,
): CanonicalRelationshipReviewDocumentHistory {
  const response = record(value);
  exactKeys(response, [
    "reviewId", "relationshipRequirementId", "documentId", "decisions",
  ]);
  if (!Array.isArray(response.decisions)) invalid();
  const reviewId = uuid(response.reviewId);
  const relationshipRequirementId = uuid(response.relationshipRequirementId);
  const documentId = uuid(response.documentId);
  const decisions = response.decisions.map((value) => {
    const row = record(value);
    exactKeys(row, [
      "id", "review_id", "review_requirement_id", "document_id", "decision",
      "reason_code", "note", "reviewer_role", "reviewer_scope_id",
      "previous_decision_id", "decided_at",
    ]);
    const decision = string(row.decision);
    const reviewerRole = string(row.reviewer_role);
    const reasonCode = nullableString(row.reason_code);
    if (
      !DECISIONS.has(decision)
      || !ROLES.has(reviewerRole)
      || (reasonCode !== null && !SAFE_REASON.test(reasonCode))
    ) {
      invalid();
    }
    return Object.freeze({
      decisionId: uuid(row.id),
      reviewId: uuid(row.review_id),
      relationshipRequirementId: uuid(row.review_requirement_id),
      documentId: uuid(row.document_id),
      decision: decision as CanonicalRelationshipDecision,
      reasonCode,
      note: nullableString(row.note),
      reviewerRole:
        reviewerRole as CanonicalRelationshipReviewDocumentHistoryItem["reviewerRole"],
      reviewerScopeId: nullableUuid(row.reviewer_scope_id),
      previousDecisionId: nullableUuid(row.previous_decision_id),
      decidedAt: string(row.decided_at),
    });
  });
  if (
    decisions.some((item) =>
      item.reviewId !== reviewId
      || item.relationshipRequirementId !== relationshipRequirementId
      || item.documentId !== documentId
    )
    || decisions.some((item, index) =>
      index > 0 && decisions[index - 1].decidedAt > item.decidedAt
    )
  ) {
    invalid();
  }
  return Object.freeze({
    reviewId,
    relationshipRequirementId,
    documentId,
    decisions: Object.freeze(decisions),
  });
}

export function parseCanonicalRelationshipReviewDocumentDecisionResult(
  value: unknown,
): CanonicalRelationshipReviewDocumentDecisionResult {
  const row = record(value);
  exactKeys(row, [
    "decision_event_id", "review_id", "review_requirement_id", "document_id",
    "decision", "reason_code", "note", "reviewer_user_id",
    "reviewer_scope_id", "reviewer_role", "decided_at",
    "previous_decision_id", "request_id",
  ]);
  const decision = string(row.decision);
  const reviewerRole = string(row.reviewer_role);
  const reasonCode = nullableString(row.reason_code);
  if (
    !DECISIONS.has(decision)
    || !ROLES.has(reviewerRole)
    || (reasonCode !== null && !SAFE_REASON.test(reasonCode))
  ) {
    invalid();
  }
  return Object.freeze({
    decisionId: uuid(row.decision_event_id),
    reviewId: uuid(row.review_id),
    relationshipRequirementId: uuid(row.review_requirement_id),
    documentId: uuid(row.document_id),
    decision: decision as CanonicalRelationshipDecision,
    reasonCode,
    note: nullableString(row.note),
    reviewerUserId: uuid(row.reviewer_user_id),
    reviewerScopeId: nullableUuid(row.reviewer_scope_id),
    reviewerRole:
      reviewerRole as CanonicalRelationshipReviewDocumentDecisionResult["reviewerRole"],
    decidedAt: string(row.decided_at),
    previousDecisionId: nullableUuid(row.previous_decision_id),
    requestId: nullableString(row.request_id),
  });
}

export function parseCanonicalRelationshipReviewDocumentPreview(
  value: unknown,
): CanonicalRelationshipReviewDocumentPreview {
  const row = record(value);
  exactKeys(row, ["signedUrl", "expiresAt", "fileName", "mimeType", "byteSize"]);
  const signedUrl = string(row.signedUrl);
  if (!/^https?:\/\//i.test(signedUrl)) invalid();
  return Object.freeze({
    signedUrl,
    expiresAt: string(row.expiresAt),
    fileName: string(row.fileName),
    mimeType: string(row.mimeType),
    byteSize: count(row.byteSize),
  });
}

export function parseCanonicalRelationshipDecisionResult(
  value: unknown,
): CanonicalRelationshipDecisionResult {
  const row = record(value);
  exactKeys(row, [
    "decision_event_id", "review_id", "decision", "reason_code", "note",
    "reviewer_user_id", "reviewer_scope_id", "reviewer_role", "decided_at",
    "previous_decision_id", "request_id",
  ]);
  const decision = string(row.decision);
  const reviewerRole = string(row.reviewer_role);
  const reasonCode = nullableString(row.reason_code);
  if (
    !DECISIONS.has(decision)
    || !ROLES.has(reviewerRole)
    || (reasonCode !== null && !SAFE_REASON.test(reasonCode))
  ) invalid();
  return Object.freeze({
    decisionId: uuid(row.decision_event_id),
    reviewId: uuid(row.review_id),
    decision: decision as CanonicalRelationshipDecision,
    reasonCode,
    note: nullableString(row.note),
    reviewerUserId: uuid(row.reviewer_user_id),
    reviewerScopeId: nullableUuid(row.reviewer_scope_id),
    reviewerRole:
      reviewerRole as CanonicalRelationshipDecisionResult["reviewerRole"],
    decidedAt: string(row.decided_at),
    previousDecisionId: nullableUuid(row.previous_decision_id),
    requestId: nullableString(row.request_id),
  });
}
